import { useState, useEffect, cloneElement } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { 
  LayoutDashboard, 
  Users, 
  Search, 
  Upload, 
  BarChart3, 
  LogOut, 
  Menu, 
  X,
  Target,
  ChevronDown,
  CreditCard,
  Gauge,
  CheckSquare,
  Crown,
  GitBranch,
  Settings,
  GraduationCap,
  AlertTriangle,
  TrendingUp,
  Eye,
  Sparkles,
  FileText,
  FolderKanban,
  HelpCircle,
  CalendarDays,
  Activity,
  Phone,
  Mail,
  Send,
  Wand2,
  Inbox,
  Trophy
} from "lucide-react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import ChatWidget from "./ChatWidget";
import HelpButton from "./HelpButton";
import GlobalEmailComposer from "./GlobalEmailComposer";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const navItems = [
  { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { path: "/leads", label: "Leads", icon: Target },
  { path: "/tasks", label: "Tasks", icon: CheckSquare },
  { path: "/goals", label: "Sales Goals", icon: Trophy },
  { path: "/projects", label: "Project Manager", icon: FolderKanban },
  { path: "/calendar", label: "Calendar", icon: CalendarDays },
  { path: "/calls", label: "Calls", icon: Phone },
  { path: "/activity", label: "Activity", icon: Activity },
  { path: "/business-search", label: "Find Businesses", icon: Search },
  { path: "/sales-intelligence", label: "Sales Intel", icon: Sparkles },
  { path: "/campaigns", label: "AI Campaigns", icon: Wand2 },
  { path: "/campaigns/replies", label: "Campaign Replies", icon: Inbox },
  { path: "/sent-emails", label: "Sent Emails", icon: Send },
  { path: "/email-templates", label: "Email Templates", icon: FileText },
  { path: "/import", label: "Import", icon: Upload },
  { path: "/usage", label: "Usage", icon: Gauge },
  { path: "/team", label: "Team", icon: Users, roles: ["owner", "admin", "sales_manager"] },
  { path: "/team/hierarchy", label: "Hierarchy", icon: GitBranch, roles: ["owner", "admin", "sales_manager"] },
  { path: "/reports", label: "Reports", icon: BarChart3, roles: ["owner", "admin", "sales_manager"] },
  { path: "/analytics", label: "Analytics", icon: TrendingUp, roles: ["owner", "admin", "sales_manager"] },
  { path: "/manager-oversight", label: "Team Oversight", icon: Eye, roles: ["owner", "admin", "sales_manager"] },
  { path: "/billing", label: "Billing", icon: CreditCard, roles: ["owner", "admin"] },
  { path: "/settings/mail", label: "Mail Settings", icon: Mail, roles: ["owner", "admin"] },
  { path: "/learning", label: "Learning Center", icon: GraduationCap },
  { path: "/help", label: "Help Center", icon: HelpCircle },
  { path: "/platform-admin", label: "Platform Admin", icon: Crown, systemRole: "platform_owner" },
];

export default function Layout({ children, user }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [emailComposerOpen, setEmailComposerOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Initialize impersonation state from localStorage (read during render)
  const [impersonation] = useState(() => {
    const storedImpersonation = localStorage.getItem("impersonation");
    if (storedImpersonation) {
      try {
        return JSON.parse(storedImpersonation);
      } catch (e) {
        localStorage.removeItem("impersonation");
        return null;
      }
    }
    return null;
  });

  const handleExitImpersonation = async () => {
    if (!impersonation?.token) return;
    
    try {
      const response = await fetch(`${API}/platform/users/${user?.user_id}/exit-impersonation`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ token: impersonation.token })
      });
      
      if (response.ok) {
        localStorage.removeItem("impersonation");
        toast.success("Exited impersonation mode");
        window.location.href = "/platform-admin";
      } else {
        toast.error("Failed to exit impersonation");
      }
    } catch (error) {
      toast.error("Failed to exit impersonation");
    }
  };

  const handleLogout = async () => {
    try {
      // Clear impersonation on logout
      localStorage.removeItem("impersonation");
      await fetch(`${API}/auth/logout`, {
        method: "POST",
        credentials: "include"
      });
    } catch (error) {
      console.error("Logout error:", error);
    }
    navigate("/login");
  };

  const filteredNavItems = navItems.filter(item => {
    // Check system role requirement (e.g., platform_owner for Platform Admin)
    if (item.systemRole) {
      return user?.system_role === item.systemRole;
    }
    // Check organization role requirement
    if (item.roles) {
      return item.roles.includes(user?.role);
    }
    return true;
  });

  const getInitials = (name) => {
    if (!name) return "U";
    return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
  };

  // Clone children with user prop
  const childrenWithUser = cloneElement(children, { user });

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`fixed top-0 left-0 z-50 h-full w-64 bg-white border-r border-slate-200 transform transition-transform duration-200 lg:translate-x-0 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex items-center justify-between h-16 px-4 border-b border-slate-200">
          <Link to="/dashboard" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-primary rounded-md flex items-center justify-center">
              <Target className="w-5 h-5 text-white" />
            </div>
            <span className="font-heading text-xl font-bold tracking-tight text-slate-900">
              SalesCRMCoach
            </span>
          </Link>
          <button 
            className="lg:hidden p-1 hover:bg-slate-100 rounded"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <nav className="p-4 space-y-1 overflow-y-auto" style={{ height: "calc(100vh - 64px - 80px)" }}>
          {filteredNavItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path || 
              (item.path === "/leads" && location.pathname.startsWith("/leads/")) ||
              (item.path === "/billing" && location.pathname.startsWith("/billing/")) ||
              (item.path === "/learning" && location.pathname.startsWith("/learning"));
            
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`sidebar-link ${isActive ? "active" : ""}`}
                onClick={() => setSidebarOpen(false)}
                data-testid={`nav-${item.label.toLowerCase().replace(" ", "-")}`}
              >
                <Icon className="w-5 h-5" />
                <span className="flex-1">{item.label}</span>
              </Link>
            );
          })}
        </nav>

        {/* User section at bottom */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-200 bg-white">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button 
                className="flex items-center gap-3 w-full p-2 rounded-md hover:bg-slate-100 transition-colors"
                data-testid="user-menu-trigger"
              >
                <Avatar className="h-9 w-9">
                  <AvatarImage src={user?.picture} alt={user?.name} />
                  <AvatarFallback className="bg-primary text-primary-foreground text-sm font-medium">
                    {getInitials(user?.name)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 text-left">
                  <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
                  <p className="text-xs text-slate-500 capitalize">{user?.role?.replace("_", " ")}</p>
                </div>
                <ChevronDown className="w-4 h-4 text-slate-400" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem 
                onClick={() => navigate("/settings")}
                className="cursor-pointer"
                data-testid="settings-btn"
              >
                <Settings className="w-4 h-4 mr-2" />
                Account Settings
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                onClick={handleLogout}
                className="text-red-600 focus:text-red-600 cursor-pointer"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Impersonation Banner */}
        {impersonation && (
          <div className="bg-amber-500 text-white px-4 py-2 flex items-center justify-between" data-testid="impersonation-banner">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              <span className="font-medium">
                Logged in as {impersonation.target_user_name || user?.name} by {impersonation.admin_name}
              </span>
            </div>
            <Button 
              variant="secondary" 
              size="sm"
              onClick={handleExitImpersonation}
              data-testid="exit-impersonation-btn"
            >
              Exit Impersonation
            </Button>
          </div>
        )}

        {/* Top bar */}
        <header className="sticky top-0 z-30 h-16 bg-white border-b border-slate-200 flex items-center px-4 lg:px-6">
          <button 
            className="lg:hidden p-2 hover:bg-slate-100 rounded-md mr-3"
            onClick={() => setSidebarOpen(true)}
            data-testid="mobile-menu-btn"
          >
            <Menu className="w-5 h-5" />
          </button>
          
          <div className="flex-1" />
          
          {/* Quick actions */}
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setEmailComposerOpen(true)}
              data-testid="navbar-new-email-btn"
              title="Compose a new email"
            >
              <Mail className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">New Email</span>
            </Button>
            <Button 
              variant="default" 
              size="sm"
              onClick={() => navigate("/business-search")}
              className="hidden sm:flex"
              data-testid="quick-search-btn"
            >
              <Search className="w-4 h-4 mr-2" />
              Find Businesses
            </Button>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 lg:p-6">
          {childrenWithUser}
        </main>
      </div>

      {/* Global email composer */}
      <GlobalEmailComposer open={emailComposerOpen} onOpenChange={setEmailComposerOpen} />

      {/* Floating Chat Widget */}
      <HelpButton />
      <ChatWidget user={user} />
    </div>
  );
}
